import {
  GroupPermissionsProps,
  GroupPermissionsRows,
} from "../../../Pages/admin/Settings/SettingsRolesAndPermissions/settings-roles-and-permissions";
import {
  UsersPermissionsProps,
  UsersPermissionsRows,
} from "../../../Pages/admin/Settings/SettingsUsers/settings-users";
import ApiClient from "../api-client";
import { cacheService, EnvType, setupService } from "../services";
import SetupApiClient from "../Setup/setup-api-client";
import { AclTypes } from "../Types/acl-types";
import { ApproveWishesRequest } from "../Types/approve-wishes-request";
import { ChangePasswordRequest } from "../Types/change-password-request";
import { CreateGroupRequest } from "../Types/create-group-request";
import { CreateGroupRoleRequest } from "../Types/create-group-role-request";
import { DbMailingListInfos } from "../Types/db-mailing-list-infos";
import { DbPlatformAlertInfos } from "../Types/db-platform-alert-infos";
import { DbSearchAdvancedQueryInfos } from "../Types/db-search-advanced-query-infos";
import { DeclinesWishlistRequest } from "../Types/declines-wishlist-request";
import { ForgotPasswordRequest } from "../Types/forgot-password-request";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetAclsMappingResponse } from "../Types/get-acls-mapping-response";
import { GetAlertsResponse } from "../Types/get-alerts-response";
import { GetAppInfoResponse } from "../Types/get-app-info-response";
import { GetAuthModesRequest } from "../Types/get-auth-modes-request";
import { GetAuthModesResponse } from "../Types/get-auth-modes-response";
import { GetAvailableSearchQueriesResponse } from "../Types/get-available-search-queries-response";
import { GetCofResponse } from "../Types/get-cof-response";
import { GetEntitiesListResponse } from "../Types/get-entities-list-response";
import { GetGroupEnumsResponse } from "../Types/get-group-enums-response";
import { GetGroupRolesListResponse } from "../Types/get-group-roles-list-response";
import { GetGroupedUsersListAdvancedResponse } from "../Types/get-grouped-users-list-advanced-response";
import {
  FollowerDto,
  FollowersGroupedDto,
  GetGroupedUsersListBasicResponse,
} from "../Types/get-grouped-users-list-basic-response";
import { GetGroupsListResponse } from "../Types/get-groups-list-response";
import { GetLdapUsersResponse } from "../Types/get-ldap-users-response";
import { MailingListDto } from "../Types/get-mailing-list-response";
import { GetNotesResponses } from "../Types/get-notes-response";
import { GetRecentEmailsResponse, RecentEmailContext } from "../Types/get-recent-emails-response";
import { GetSearchHistoryResponse } from "../Types/get-search-history-response";
import { GetSharedAccountsResponse } from "../Types/get-shared-accounts-response";
import { GetUserResponse } from "../Types/get-user-response";
import { GetUsersListAdvancedResponse } from "../Types/get-users-list-advanced-response";
import { GetUsersListBasicResponse } from "../Types/get-users-list-basic-response";
import { GetWishResponse } from "../Types/get-wish-response";
import { GetWishlistResponse } from "../Types/get-wishlist-response";
import { GroupAdvancedInfos } from "../Types/group-advanced-infos";
import { GroupedUserAdvancedInfos } from "../Types/grouped-user-advanced-infos";
import { LoginRequest } from "../Types/login-request";
import { LoginResponse } from "../Types/login-response";
import { MfaValidateTokenRequest } from "../Types/mfa-validate-token-request";
import { PlatformResourceType } from "../Types/platform-resource-type";
import { SyncLdapUsersRequest } from "../Types/sync-ldap-users-request";
import { UpdateGroupsRequest } from "../Types/update-groups-request";
import { UpdateNotesRequest } from "../Types/update-notes-request";
import { UpdatePasswordRequest } from "../Types/update-password-request";
import { UserAdvancedInfos } from "../Types/user-advanced-infos";
import { VerifyOrResetPasswordUserRequest } from "../Types/user-verify-or-reset-password-request";
import { UserVerifyOrResetPasswordResponse } from "../Types/user-verify-or-reset-password-response";
import UserApiClient from "./user-api-client";

export default class UserService {
  userApiClient: UserApiClient;
  setupApiClient: SetupApiClient;

  constructor(envType: EnvType) {
    this.userApiClient = new UserApiClient(new ApiClient(envType), envType);
    this.setupApiClient = new SetupApiClient(new ApiClient(envType), envType);
  }

  async ValidateMfaToken(request: MfaValidateTokenRequest): Promise<LoginResponse> {
    const response = await this.userApiClient.ValidateMfaToken(request);

    // Check login attempt
    cacheService.SetAuth(response);
    cacheService.SetPlatformSettings(response.platformSettings);

    return response;
  }

  async Login(login: LoginRequest): Promise<LoginResponse> {
    const response = await this.userApiClient.Login(login);

    console.log("Login() -> server OK.");

    // Check login attempt
    cacheService.SetAuth(response);
    cacheService.SetPlatformSettings(response.platformSettings);

    console.log(
      "Login() -> authenticated as userId = [" +
        response?.user?.id +
        "] successfully until " +
        response?.sessionCreated?.expireDate +
        "."
    );

    return response;
  }

  async LoginSharedCrossAccount(userId: number): Promise<LoginResponse> {
    const response = await this.userApiClient.LoginSharedCrossAccount(userId);

    console.log("LoginShared() -> server OK.");

    // Check login attempt
    cacheService.SetAuth(response);
    cacheService.SetPlatformSettings(response.platformSettings);

    console.log(
      "Login() -> authenticated as userId = [" +
        response?.user?.id +
        "] successfully until " +
        response?.sessionCreated?.expireDate +
        "."
    );

    return response;
  }

  async LoginByAccessKey(key: string): Promise<LoginResponse> {
    const response = await this.userApiClient.LoginByAccessKey(key);

    console.log("LoginByAccessKey() -> server OK.");

    // Check login attempt
    cacheService.SetAuth(response);
    cacheService.SetPlatformSettings(response.platformSettings);

    console.log(
      "Login() -> authenticated as userId = [" +
        response?.user?.id +
        "] successfully until " +
        response?.sessionCreated?.expireDate +
        "."
    );

    return response;
  }

  async SetDataOnLoginSuccess(): Promise<void> {
    try {
      await this.SetUserDataOnLoginSuccess();
      await this.SetMetadataOnLoginSuccess();
    } catch (err) {
      console.error(err);
    }
  }

  // Called once Login() is successful
  async SetUserDataOnLoginSuccess(): Promise<void> {
    try {
      const appData = await this.userApiClient.GetAppInfo();
      const sharedAccounts = await this.userApiClient.GetSharedAccounts();

      cacheService.SetAppData(appData.appInfo);
      cacheService.SetSharedAccounts(sharedAccounts.accounts);
      cacheService.Resync();
    } catch (err) {
      console.error(err);
    }
  }

  async SetMetadataOnLoginSuccess(): Promise<void> {
    const metadataFields = await setupService.GetAllMetadataFields();
    try {
      cacheService.SetMetadataFields(metadataFields.metadataFields ?? null);
    } catch (err) {
      console.error(err);
    }
  }

  async GetMailingList(): Promise<MailingListDto[]> {
    const response = await this.userApiClient.GetMailingList();

    if (!response || !response.mailinglists) return [];

    return response.mailinglists.map((mailingList) => ({
      name: mailingList.name!,
      emails: mailingList.emails!,
    }));
  }

  async GetAllEmailsAsStringList(): Promise<string[]> {
    const response = await this.userApiClient.GetAllUsersBasic();

    if (!response || !response.users) return [];

    let emails = response.users?.filter((x) => x.email !== null).map((x) => x.email!);
    return emails.reduce<string[]>((acc, value) => {
      if (!acc.includes(value)) {
        acc.push(value);
      }
      return acc;
    }, []);
  }

  async GetDetailedMailingList(): Promise<DbMailingListInfos[]> {
    const response = await this.userApiClient.GetMailingList();

    if (!response || !response.mailinglists) return [];

    return response.mailinglists;
  }

  async GetUsersEmailsFromSearh(searchValue: string): Promise<string[]> {
    const response = await this.userApiClient.SearchUsersBasic(searchValue);
    if (!response.users || response.users === null || response.users.length === 0) return [];

    // this below returns string | undefined
    // var mappedEmails = response.users.map((user) => user.email);
    // this below returns string | null, even with the check...
    // var baba = mappedEmails.filter(x => x !== undefined && x !== null);
    // so we do a foreach ¯\_(ツ)_/¯

    const output: string[] = [];
    response.users.forEach((user) => {
      if (user.email && user.email !== null && user.name && user.name !== null)
        output.push(`${user.email} - ${user.name}`);
    });
    return output;
  }

  async GetModalSetFollowersData(): Promise<FollowersGroupedDto[]> {
    const groupedUsersList = (await this.userApiClient.GetUsersBasicList()).groupedUsers;

    let output: FollowersGroupedDto[] = [];

    groupedUsersList?.forEach((groupedUsers) => {
      let followers: FollowerDto[] = [];
      if (groupedUsers.users) {
        groupedUsers.users.forEach((user) => {
          if (user && user.enabled)
            followers.push({
              id: user.id,
              name: user.name ?? "Unknown",
              login: user.login,
              enabled: user.enabled,
            });
        });
        if (groupedUsers.group && groupedUsers.group.name)
          output.push({
            groupName: groupedUsers.group.name,
            followers,
          });
      }
    });

    return output.sort((a, b) => {
      const nameA = a.groupName.toLowerCase();
      const nameB = b.groupName.toLowerCase();
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });
  }

  async GetGroupsListForRolesAndPermissions(): Promise<GroupPermissionsProps | null> {
    const groupInfos = (await this.userApiClient.GetGroupsList()).groups;
    if (groupInfos) {
      const computedInstances = this.MapInstanceInfosToGroupsRolesPermissionsRows(groupInfos);
      const data: GroupPermissionsProps = {
        rows: computedInstances,
      };
      return data;
    }
    return null;
  }

  async GetUsersListForRolesAndPermissions(): Promise<UsersPermissionsProps | null> {
    const usersInfos = (await this.userApiClient.GetUsersAdvancedList()).groupedUsers;
    if (usersInfos) {
      const computedInstances = this.MapInstanceInfosToUsersRolesPermissionsRows(usersInfos);
      const data: UsersPermissionsProps = {
        rows: computedInstances,
        filtersData: {
          groups:
            usersInfos
              .map((data) => {
                if (data.group) return data.group;
              })
              .sort((a, b) => a!.name!.localeCompare(b?.name!)) || [],
        },
      };
      return data;
    }
    return null;
  }

  public UpdateUsersGroup(users: UserAdvancedInfos[], groupId: number): void {
    const usersToPatch: UserAdvancedInfos[] = JSON.parse(JSON.stringify(users));
    usersToPatch.forEach((user) => {
      if (user.userInfos) {
        user.userInfos.groupId = groupId;
        this.userApiClient.UpdateUser(user);
      }
    });
  }

  async GetGroupRolesList(): Promise<GetGroupRolesListResponse> {
    return await this.userApiClient.GetGroupRolesList();
  }

  async CreateGroupRole(request: CreateGroupRoleRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.CreateGroupRole(request);
  }

  async UpdateGroupRole(request: CreateGroupRoleRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateGroupRole(request);
  }

  async DeleteGroupRole(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteGroupRole(id);
  }

  async LoginByKey(key: string): Promise<LoginResponse> {
    const response = await this.userApiClient.LoginByKey(key);
    if (!response.success || response.user === null) return Promise.reject("Wrong authentication key");

    console.log("LoginByKey() -> server OK.");

    // Check login attempt
    cacheService.SetAuth(response);
    cacheService.SetPlatformSettings(response.platformSettings);

    console.log(
      "LoginByKey() -> authenticated as userId = [" +
        response?.user?.id +
        "] successfully until " +
        response?.sessionCreated?.expireDate +
        "."
    );

    return response;
  }

  //------ NOT USED------
  async Logout(): Promise<GenericResponseStd> {
    return await this.userApiClient.Logout();
  }
  async LoginEmulate(userId: number): Promise<LoginResponse> {
    return await this.userApiClient.LoginEmulate(userId);
  }
  async GetSharedAccounts(): Promise<GetSharedAccountsResponse> {
    return await this.userApiClient.GetSharedAccounts();
  }
  GetPlatformIcon(url: string): string {
    return this.userApiClient.GetPlatformIcon(url);
  }
  GetPlatformIconById(id: number): string {
    return this.userApiClient.GetPlatformIconById(id);
  }
  GetPlatformResourceImage(): string {
    return this.userApiClient.GetPlatformResourceImage(PlatformResourceType.LeftMenuLogo);
  }
  GetPlatformResourceImageByAssetShareAuthKey(type: PlatformResourceType, shareKey: string): string {
    return this.userApiClient.GetPlatformResourceImageByAssetShareAuthKey(type, shareKey);
  }
  GetPlatformResourceImageByMediaShareAuthKey(type: PlatformResourceType, shareKey: string): string {
    return this.userApiClient.GetPlatformResourceImageByMediaShareAuthKey(type, shareKey);
  }
  async GetAppInfo(): Promise<GetAppInfoResponse> {
    return await this.userApiClient.GetAppInfo();
  }
  async GetAuthModes(hostname: string): Promise<GetAuthModesResponse | null> {
    return await this.userApiClient.GetAuthModes(new GetAuthModesRequest({ hostname }));
  }
  async GetActiveAlertsList(): Promise<GetAlertsResponse> {
    return await this.userApiClient.GetActiveAlertsList();
  }
  async GetAllAlertsList(): Promise<GetAlertsResponse> {
    return await this.userApiClient.GetAllAlertsList();
  }
  async CreateAlert(request: DbPlatformAlertInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.CreateAlert(request);
  }
  async UpdateAlert(request: DbPlatformAlertInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateAlert(request);
  }
  async DeleteAlert(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteAlert(id);
  }
  async CreateUser(user: UserAdvancedInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.CreateUser(user);
  }
  async UpdateUser(user: UserAdvancedInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateUser(user);
  }
  async UpdatePasswordBySession(user: UpdatePasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdatePasswordBySession(user);
  }
  async DeleteUser(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteUser(id);
  }
  async SearchUsers(searchPattern: string): Promise<GetUsersListAdvancedResponse> {
    return await this.userApiClient.SearchUsers(searchPattern);
  }
  async SearchUsersBasic(searchPattern: string): Promise<GetUsersListBasicResponse> {
    return await this.userApiClient.SearchUsersBasic(searchPattern);
  }
  async GetUsersAdvancedList(): Promise<GetGroupedUsersListAdvancedResponse> {
    return await this.userApiClient.GetUsersAdvancedList();
  }
  async GetAllUsersBasic(includeExternal?: boolean): Promise<GetUsersListBasicResponse> {
    return await this.userApiClient.GetAllUsersBasic(includeExternal);
  }
  async GetLdapUsers(): Promise<GetLdapUsersResponse> {
    return await this.userApiClient.GetLdapUsers();
  }
  async SyncLdapUsers(request: SyncLdapUsersRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.SyncLdapUsers(request);
  }
  async DownloadUsersAsCsv(): Promise<GenericResponseStd> {
    return await this.userApiClient.DownloadUsersAsCsv();
  }
  async GetMyNotes(): Promise<GetNotesResponses> {
    return await this.userApiClient.GetMyNotes();
  }
  async UpdateMyNotes(text: UpdateNotesRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateMyNotes(text);
  }
  async GetAvailableSearchQueries(): Promise<GetAvailableSearchQueriesResponse> {
    return await this.userApiClient.GetAvailableSearchQueries();
  }
  async PutSearchQueries(request: DbSearchAdvancedQueryInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.PutSearchQueries(request);
  }
  async DeleteSearchQuery(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteSearchQuery(id);
  }
  async GetGroupsEnums(): Promise<GetGroupEnumsResponse> {
    return await this.userApiClient.GetGroupsEnums();
  }
  async GetGroupsList(): Promise<GetGroupsListResponse> {
    return await this.userApiClient.GetGroupsList();
  }
  async GetAclsMappingList(): Promise<GetAclsMappingResponse> {
    return await this.userApiClient.GetAclsMappingList();
  }
  async GetUsersListByGroupId(id: number): Promise<GetUsersListAdvancedResponse> {
    return await this.userApiClient.GetUsersListByGroupId(id);
  }
  async GetUsersListByProviderId(id: number): Promise<GetUsersListBasicResponse> {
    return await this.userApiClient.GetUsersListByProviderId(id);
  }
  async GetUsersListByCustomerId(id: number): Promise<GetUsersListAdvancedResponse> {
    return await this.userApiClient.GetUsersListByCustomerId(id);
  }

  async EditProfilePicture(
    file: File,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GetUserResponse> {
    const formData = new FormData();
    formData.append("file", file);
    return await this.userApiClient.EditProfilePicture(formData, abortSignal, onUploadProgressCallback);
  }

  async EditPlatformIcon(
    file: File,
    type: PlatformResourceType,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    return await this.userApiClient.EditPlatformIcon(formData, abortSignal, onUploadProgressCallback);
  }

  async CreateGroup(request: CreateGroupRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.CreateGroup(request);
  }
  async UpdateGroup(request: CreateGroupRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateGroup(request);
  }
  async UpdateGroups(request: UpdateGroupsRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateGroups(request);
  }
  async DeleteGroup(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteGroup(id);
  }
  async GetEntitiesList(): Promise<GetEntitiesListResponse> {
    return await this.userApiClient.GetEntitiesList();
  }
  async CreateMailingList(request: DbMailingListInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.CreateMailingList(request);
  }
  async UpdateMailingList(request: DbMailingListInfos): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateMailingList(request);
  }
  async DeleteMailingList(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteMailingList(id);
  }
  async ForgotPasswordRequest(request: ForgotPasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.ForgotPasswordRequest(request);
  }
  async ForgotPasswordReset(request: VerifyOrResetPasswordUserRequest): Promise<UserVerifyOrResetPasswordResponse> {
    return await this.userApiClient.ForgotPasswordReset(request);
  }
  async ChangePassword(request: ChangePasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.ChangePassword(request);
  }
  async UpdateMyPassword(request: UpdatePasswordRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.UpdateMyPassword(request);
  }
  async GetWishlist(): Promise<GetWishlistResponse> {
    return await this.userApiClient.GetWishlist();
  }
  async GetPagedWishlist(pageNb: number, pageSize: number): Promise<GetWishlistResponse> {
    return await this.userApiClient.GetPagedWishlist(pageNb, pageSize);
  }
  async GetWishById(id: number): Promise<GetWishResponse> {
    return await this.userApiClient.GetWishById(id);
  }
  async ApproveWishes(request: ApproveWishesRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.ApproveWishes(request);
  }
  async DeclineWishes(request: DeclinesWishlistRequest): Promise<GenericResponseStd> {
    return await this.userApiClient.DeclineWishes(request);
  }
  async DeleteWish(id: number): Promise<GenericResponseStd> {
    return await this.userApiClient.DeleteWish(id);
  }
  async GetSearchHistory(): Promise<GetSearchHistoryResponse> {
    return await this.userApiClient.GetSearchHistory();
  }
  async GetCof(platformId?: number): Promise<GetCofResponse> {
    if (platformId) return await this.userApiClient.GetCofNoAuth(platformId);

    return await this.userApiClient.GetCof();
  }
  async PutCof(): Promise<GenericResponseStd> {
    return await this.userApiClient.PutCof();
  }
  async GetRecentEmails(context: RecentEmailContext): Promise<GetRecentEmailsResponse> {
    return await this.userApiClient.GetRecentEmails(context);
  }
  async VerifyUser(request: VerifyOrResetPasswordUserRequest): Promise<UserVerifyOrResetPasswordResponse> {
    return this.userApiClient.VerifyUser(request);
  }
  async ResendVerifyEmail(userId: number): Promise<UserVerifyOrResetPasswordResponse> {
    return this.userApiClient.ResendVerifyEmail(userId);
  }

  async GetUsersBasicList(): Promise<GetGroupedUsersListBasicResponse> {
    return await this.userApiClient.GetUsersBasicList();
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.userApiClient.GetApiUrl();
  }

  GetUserAvatarProfileUrl(userId: number): string {
    return this.userApiClient.GetApiUrl() + "/users/" + userId + "/picture/" + cacheService.GetAuthKey();
  }

  GetResourceUrl(type: PlatformResourceType): string {
    return (
      this.userApiClient.GetApiUrl() + "/platforms/resources/image-render/" + type + "/" + cacheService.GetAuthKey()
    );
  }

  //------Private------
  private MapInstanceInfosToGroupsRolesPermissionsRows(groupInfos: GroupAdvancedInfos[]): GroupPermissionsRows[] {
    let mappedData: GroupPermissionsRows[] = [];
    let totalAcls = Object.keys(AclTypes).length;
    groupInfos.forEach((data) => {
      mappedData.push({
        id: data.id,
        usersCount: data.usersCount,
        groupName: data.group?.name,
        permissions: data.group?.acls || [],
        permissionsNumber: (data.group?.acls ? data.group?.acls?.length : "0") + "/" + totalAcls,
        groupFull: data,
        roleId: data.group?.roleId,
      });
    });

    return mappedData;
  }

  private MapInstanceInfosToUsersRolesPermissionsRows(usersInfos: GroupedUserAdvancedInfos[]): UsersPermissionsRows[] {
    let mappedData: UsersPermissionsRows[] = [];
    usersInfos.forEach((data) => {
      data.users?.forEach((user) => {
        mappedData.push({
          id: user.userInfos!.id,
          aclExceptions: 0,
          groupId: user.userInfos!.groupId,
          roleId: data.group!.roleId,
          groupName: data.group?.name ?? "",
          firstName: user.userInfos!.firstName,
          lastName: user.userInfos!.lastName,
          enabled: user.userInfos!.enabled,
          expireDate: user.userInfos!.expireDate?.toString() || null,
          login: user.userInfos!.login,
          email: user.userInfos!.email,
          userAdvancedInfos: user,
          customerLinksNames: user.userCustomersLinks?.map((x) => x.name) || [],
          authMode: user.userInfos?.authMode!,
          isVerified: user.userInfos?.isVerified!,
        });
      });
    });

    return mappedData;
  }
}
